import find from 'lodash/find';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  ButtonTextWrapper,
  ChevronIcon,
  DropdownPlaceholder,
  MenuItem,
  OpenerButton,
  ScrollingMenu,
  Search,
} from './dropdown_menu';
import PopoverMenu, { usePopoverMenu } from 'components/common/menu';

export default function AsyncDropdownMenu({
  className,
  'data-aid': dataAid,
  delay,
  isError,
  labelsHash = {},
  maxHeight,
  maxRows,
  maxWidth,
  onSearch,
  onSelect,
  options,
  placeholder,
  text,
  value,
}) {
  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  useEffect(() => onSearch(''), [isOpen, onSearch]);
  // Because the label of the selected item depends on the options, and to prevent
  // it from being reset when the options are changed, the hash of memo labels is used
  const [memoizedLabels, setMemoizedLabels] = useState({});

  useEffect(() => {
    setMemoizedLabels({
      ...memoizedLabels,
      ...labelsHash,
    });
  }, [labelsHash]);

  useEffect(() => {
    if (value && (!memoizedLabels[value] || memoizedLabels[value] === value)) {
      setMemoizedLabels({
        ...memoizedLabels,
        [value]: get(find(options, { value }), 'label', memoizedLabels[value] || value),
      });
    }
  }, [options, setMemoizedLabels, value]);

  const buttonText = text ? (
    text
  ) : value ? (
    memoizedLabels[value]
  ) : (
    <DropdownPlaceholder>{placeholder}</DropdownPlaceholder>
  );

  return (
    <React.Fragment>
      <OpenerButton
        className={className}
        data-aid={dataAid ? `${dataAid}-opener` : undefined}
        fitOpenerWidth
        isError={isError}
        onClick={onToggle}
        ref={setTargetRef}
      >
        <ButtonTextWrapper>{buttonText}</ButtonTextWrapper>
        <ChevronIcon data-isopen={isOpen} />
      </OpenerButton>
      <PopoverMenu
        data-aid={dataAid}
        fitOpenerWidth
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        targetPosition="start"
        targetRef={targetRef}
      >
        <Search delay={delay} onChange={onSearch} placeholder={placeholder} />
        <ScrollingMenu fitOpenerWidth maxHeight={maxHeight} maxRows={maxRows} maxWidth={maxWidth}>
          {options.map(option => (
            <MenuItem
              data-aid={`menuOption-${option.value}`}
              isFocused={Boolean(value && option.value === value)}
              key={option.value}
              onClose={onClose}
              onSelect={onSelect}
              option={option}
            />
          ))}
        </ScrollingMenu>
      </PopoverMenu>
    </React.Fragment>
  );
}

AsyncDropdownMenu.propTypes = {
  className: PropTypes.string,
  'data-aid': PropTypes.string,
  delay: PropTypes.number,
  labelsHash: PropTypes.objectOf(PropTypes.string),
  maxRows: PropTypes.number,
  maxHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.node.isRequired,
      text: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
    })
  ),
  placeholder: PropTypes.string,
  text: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};
